import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import { AppNotification } from 'components/views/company/outreach-template/proposals/types';
import { FC, useMemo } from 'react';
import { NotificationRow } from './notification-row';

interface NotificationListProps {
  /** All notifications for the current user — used to derive the filter options. */
  items: AppNotification[];
  /** Items after the active filter is applied — rendered as rows. */
  filteredItems: AppNotification[];
  filter: string;
  onFilterChange: (filter: string) => void;
  onItemClick: (id: string) => void;
}

const ALL_FILTER = 'all';

const labelForRefType = (refType: string): string => {
  if (refType === 'proposal') return 'Proposals';
  if (!refType) return 'Other';
  return refType.charAt(0).toUpperCase() + refType.slice(1) + 's';
};

export const NotificationList: FC<NotificationListProps> = ({
  items,
  filteredItems,
  filter,
  onFilterChange,
  onItemClick,
}) => {
  const refTypes = useMemo(() => Array.from(new Set(items.map((n) => n.refType))).sort(), [items]);

  if (items.length === 0) {
    return (
      <Box sx={{ px: 2, py: 4 }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          You&apos;re all caught up.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ px: 2, py: 1 }}>
        <Select
          size="small"
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value={ALL_FILTER}>All notifications</MenuItem>
          {refTypes.map((rt) => (
            <MenuItem key={rt} value={rt}>
              {labelForRefType(rt)}
            </MenuItem>
          ))}
        </Select>
      </Box>
      {filteredItems.length === 0 ? (
        <Box sx={{ px: 2, py: 4 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No notifications match this filter.
          </Typography>
        </Box>
      ) : (
        <Stack direction="column" divider={<Box sx={{ height: '1px', bgcolor: 'divider' }} />}>
          {filteredItems.map((n) => (
            <NotificationRow key={n.id} notification={n} onClick={() => onItemClick(n.id)} />
          ))}
        </Stack>
      )}
    </>
  );
};
